import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Brain, 
  Heart,
  Calendar,
  Clock,
  Users,
  BookOpen,
  Music,
  Coffee,
  Plus,
  ChevronRight,
  Sparkles
} from 'lucide-react';

const wellnessActivities = [
  {
    title: 'Physical Exercise',
    description: 'Regular physical activity for mental and physical wellbeing',
    icon: <Activity size={24} />,
    color: 'from-idecide-primary/20 to-idecide-accent/20',
    textColor: 'text-idecide-primary',
    suggestions: [
      '30-minute walk',
      'Yoga session',
      'Swimming',
      'Cycling'
    ]
  },
  {
    title: 'Mindfulness',
    description: 'Techniques for mindfulness and meditation',
    icon: <Brain size={24} />,
    color: 'from-idecide-accent/20 to-idecide-primary/20',
    textColor: 'text-idecide-accent',
    suggestions: [
      '10-minute meditation',
      'Deep breathing exercises',
      'Body scan',
      'Mindful walking'
    ]
  },
  {
    title: 'Social Connection',
    description: 'Activities to maintain social wellbeing',
    icon: <Users size={24} />,
    color: 'from-idecide-primary/20 to-idecide-accent/20',
    textColor: 'text-idecide-primary',
    suggestions: [
      'Coffee with a friend',
      'Group activity',
      'Family time',
      'Community event'
    ]
  },
  {
    title: 'Self-Care',
    description: 'Activities to nurture mental and emotional health',
    icon: <Heart size={24} />,
    color: 'from-idecide-accent/20 to-idecide-primary/20',
    textColor: 'text-idecide-accent',
    suggestions: [
      'Reading a book',
      'Journaling',
      'Art therapy',
      'Bath time'
    ]
  },
  {
    title: 'Learning',
    description: 'Mental stimulation and personal growth',
    icon: <BookOpen size={24} />,
    color: 'from-idecide-primary/20 to-idecide-accent/20',
    textColor: 'text-idecide-primary',
    suggestions: [
      'Online course',
      'Skill development',
      'Language learning',
      'Book club'
    ]
  },
  {
    title: 'Relaxation',
    description: 'Activities to reduce stress and promote calm',
    icon: <Coffee size={24} />,
    color: 'from-idecide-accent/20 to-idecide-primary/20',
    textColor: 'text-idecide-accent',
    suggestions: [
      'Music therapy',
      'Nature walk',
      'Tea ceremony',
      'Gentle stretching'
    ]
  }
];

const WellnessPlanner: React.FC = () => {
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scheduleDetails, setScheduleDetails] = useState({
    date: '',
    time: '',
    notes: ''
  });

  const handleOpenModal = (activity: any) => {
    setSelectedActivity(activity);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
    setScheduleDetails({
      date: '',
      time: '',
      notes: ''
    });
  };

  const handleScheduleActivity = () => {
    // Here you would typically handle the scheduling
    console.log('Activity scheduled:', { activity: selectedActivity, ...scheduleDetails });
    handleCloseModal();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-6 md:p-8">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-12"
      >
        <div className="bg-gradient-to-r from-idecide-primary to-idecide-dark rounded-3xl p-8 md:p-12 text-white shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="text-idecide-accent" size={24} />
            <h1 className="text-3xl md:text-4xl font-bold">Wellness Planner</h1>
          </div>
          <p className="text-lg md:text-xl text-idecide-primary-light max-w-2xl">
            Schedule and track your wellness activities. Choose from a variety of activities 
            to support your mental and physical wellbeing.
          </p>
        </div>
      </motion.div>

      {/* Activities Grid */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {wellnessActivities.map((activity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <button 
                onClick={() => handleOpenModal(activity)}
                className="w-full text-left"
              >
                <div className="group h-full bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${activity.color} flex items-center justify-center mb-4`}>
                    <div className={activity.textColor}>
                      {activity.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-idecide-primary transition-colors">
                    {activity.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {activity.description}
                  </p>
                  <div className="flex items-center text-idecide-primary font-medium">
                    Schedule Activity
                    <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Schedule Modal */}
      {isModalOpen && selectedActivity && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${selectedActivity.color} flex items-center justify-center`}>
                <div className={selectedActivity.textColor}>
                  {selectedActivity.icon}
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Schedule {selectedActivity.title}
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={scheduleDetails.date}
                  onChange={(e) => setScheduleDetails(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-idecide-primary focus:border-idecide-primary outline-none transition-all appearance-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={scheduleDetails.time}
                  onChange={(e) => setScheduleDetails(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-idecide-primary focus:border-idecide-primary outline-none transition-all appearance-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={scheduleDetails.notes}
                  onChange={(e) => setScheduleDetails(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-idecide-primary focus:border-idecide-primary outline-none transition-all resize-y"
                  rows={3}
                  placeholder="Add any additional notes..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleActivity}
                className="flex-1 px-4 py-2 rounded-xl bg-idecide-primary text-white font-semibold shadow-md hover:bg-idecide-primary-hover transition-colors"
              >
                Schedule
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export { WellnessPlanner }; 